import { useState, useMemo, useEffect, useRef, FC } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useStationAccess } from "@/hooks/useStationAccess";
import { StationToolLayout } from "@/components/stations/station-tool-layout";
import AppLayout from "@/components/layout/app-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FileText, AlertCircle, ArrowLeft, Loader2, Globe, Search, FileCode, CheckCircle2, Link2, ChevronDown, ChevronUp } from "lucide-react";
import { AssistantRuntimeProvider, useLocalRuntime } from "@assistant-ui/react";
import { Thread } from "@/components/assistant-ui/thread";
import "@/components/assistant-ui/assistant-ui.css";
import { createStationCopyEngineAdapter } from "@/lib/copy-chat-adapter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ThumbsRating } from "@/components/thumbs-rating";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import type { Station } from "@shared/schema";
import { Progress } from "@/components/ui/progress";
import thinkingAnimation from "@assets/Analytical Thinking Illustration_1757593408862.gif";
import { useAuthContext } from "@/contexts/AuthContext";

// Separate component for the chat interface that always calls hooks consistently
interface CopyEngineChatProps {
  station: Station;
  initialMessage: string;
  formData: CopyEngineFormValues;
  onNewCopyRequest: () => void;
}

const CopyEngineChat: FC<CopyEngineChatProps> = ({ 
  station, 
  initialMessage, 
  formData, 
  onNewCopyRequest 
}) => {
  const { getAuthToken } = useAuthContext();
  
  // Hooks are always called in this component
  const adapter = createStationCopyEngineAdapter(station, formData, getAuthToken);
  const runtime = useLocalRuntime(adapter, {
    initialMessages: [
      {
        id: "initial",
        role: "assistant" as const,
        content: [{ type: "text" as const, text: initialMessage }]
      }
    ]
  });

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Copy Engine
          </CardTitle>
          <div className="flex items-center gap-2">
            <ThumbsRating toolCategory="Copy Engine" stationId={station.id} />
            <Button
              variant="ghost"
              size="sm"
              onClick={onNewCopyRequest}
              data-testid="button-new-copy"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              New Copy Request
            </Button>
          </div>
        </div>
        <CardDescription>
          {station.copyGeneratorDisclaimer || "Generate FCC-compliant sponsorship messages for on-air use"}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 p-0 overflow-hidden">
        <div className="h-full flex flex-col">
          <AssistantRuntimeProvider runtime={runtime}>
            <Thread />
          </AssistantRuntimeProvider>
        </div>
      </CardContent>
    </Card>
  );
};

// Form schema for Copy Engine
const copyEngineFormSchema = z.object({
  sponsorName: z.string().min(1, "Sponsor name is required"),
  copyType: z.enum(["broadcast", "event"]),
  // Broadcast fields
  sponsorWebsite: z.string().optional(),
  previousCopy: z.string().optional(),
  moreInfoLink: z.string().optional(),
  // Event fields
  eventName: z.string().optional(),
  eventWebsite: z.string().optional(),
  eventDatesAndTimes: z.string().optional(),
  eventLocation: z.string().optional(),
  ticketsLink: z.string().optional(),
  // Shared field
  additionalDetails: z.string().optional(),
}).refine(
  (data) => {
    // When copyType is "event", eventLocation is required
    if (data.copyType === "event") {
      return data.eventLocation && data.eventLocation.trim().length > 0;
    }
    return true;
  },
  {
    message: "Event location is required for event copy",
    path: ["eventLocation"],
  }
).refine(
  (data) => {
    // When copyType is "event", ticketsLink is required
    if (data.copyType === "event") {
      return data.ticketsLink && data.ticketsLink.trim().length > 0;
    }
    return true;
  },
  {
    message: "More Info / Tickets link is required for event copy",
    path: ["ticketsLink"],
  }
);

type CopyEngineFormValues = z.infer<typeof copyEngineFormSchema>;

// Interface for initial message response
interface InitialMessageResponse {
  initialMessage: string;
  formData: CopyEngineFormValues;
}

export default function CopyEngine() {
  usePageTitle("Copy Engine");
  const { stationId } = useParams();
  const { hasAccess } = useStationAccess(stationId);
  const { toast } = useToast();
  const { getAuthToken } = useAuthContext();
  const [showChat, setShowChat] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string>("");
  const [formData, setFormData] = useState<CopyEngineFormValues | undefined>();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressStage, setProgressStage] = useState<string>("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [currentUrl, setCurrentUrl] = useState<string>("");
  const [progressDetails, setProgressDetails] = useState<string[]>([]);
  const [isFormMinimized, setIsFormMinimized] = useState(false);
  
  // Ref to track abort controller for in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Track the stationId that initiated the current request
  const requestStationIdRef = useRef<string | null>(null);

  // Fetch station data
  const { data: station, isLoading: stationLoading, error: stationError } = useQuery<Station>({
    queryKey: [`/api/stations/${stationId}`],
    enabled: !!stationId,
  });

  // Form setup
  const form = useForm<CopyEngineFormValues>({
    resolver: zodResolver(copyEngineFormSchema),
    defaultValues: {
      sponsorName: "",
      copyType: "broadcast",
      sponsorWebsite: "",
      previousCopy: "",
      moreInfoLink: "",
      eventName: "",
      eventWebsite: "",
      eventDatesAndTimes: "",
      eventLocation: "",
      ticketsLink: "",
      additionalDetails: "",
    },
  });

  const copyType = form.watch("copyType");

  // Reset all state when stationId changes (switching between stations)
  useEffect(() => {
    // Abort any in-flight requests from the previous station
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear the request station ref to signal station change
    requestStationIdRef.current = null;
    
    // Reset chat and form state
    setShowChat(false);
    setInitialMessage("");
    setFormData(undefined);
    
    // Reset progress state
    setIsGenerating(false);
    setProgressStage("");
    setProgressPercent(0);
    setCurrentUrl("");
    setProgressDetails([]);
    
    // Reset UI state
    setIsFormMinimized(false);
    
    // Reset form fields
    form.reset({
      sponsorName: "",
      copyType: "broadcast",
      sponsorWebsite: "",
      previousCopy: "",
      moreInfoLink: "",
      eventName: "",
      eventWebsite: "",
      eventDatesAndTimes: "",
      eventLocation: "",
      ticketsLink: "",
      additionalDetails: "",
    });
  }, [stationId]);

  // Handle SSE-based generation with real-time progress
  const generateWithProgress = async (values: CopyEngineFormValues) => {
    setIsGenerating(true);
    setProgressPercent(0);
    setProgressStage("Initializing");
    setProgressDetails([]);
    
    // Track which station this request is for
    const requestStationId = stationId || null;
    requestStationIdRef.current = requestStationId;
    
    // Get Firebase token for authentication
    const token = await getAuthToken();
    
    const maxRetries = 3;
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      // Check if station changed before each attempt
      if (requestStationIdRef.current !== requestStationId) {
        console.log('Station changed - cancelling copy generation');
        return;
      }
      
      // Initialize startTime outside try block so it's accessible in catch
      const startTime = Date.now();
      
      try {
        // Show retry status if not first attempt
        if (attempt > 1) {
          setProgressStage(`Request timed out, retrying... (attempt ${attempt}/${maxRetries})`);
          setProgressPercent(0);
          setProgressDetails([]);
          await new Promise(resolve => setTimeout(resolve, Math.min(1000 * Math.pow(2, attempt - 1), 5000))); // Exponential backoff
        }
        
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Create an AbortController with 60-second timeout
        // Store in ref so it can be aborted on station switch
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 seconds
        
        const response = await fetch(`/api/stations/${stationId}/copy-engine/generate-initial`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(values),
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        abortControllerRef.current = null;

        if (!response.ok) {
          throw new Error('Failed to generate copy');
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) throw new Error('No reader available');

        let buffer = '';
        let hasCompleted = false;
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]') continue;
              
              try {
                const parsed = JSON.parse(data);
                
                if (parsed.type === 'progress') {
                  setProgressPercent(parsed.percent);
                  setProgressStage(parsed.stage);
                  if (parsed.url) setCurrentUrl(parsed.url);
                  if (parsed.detail) {
                    setProgressDetails(prev => [...prev, parsed.detail]);
                  }
                } else if (parsed.type === 'complete') {
                  hasCompleted = true;
                  
                  // Always update state with the result - the copy is already saved to DB
                  // and tied to the correct station. Silently dropping results causes user confusion.
                  const messageContent = parsed.initialMessage;
                  const completedFormData = parsed.formData;
                  
                  // Defensive check: if we got valid data, update state immediately
                  if (messageContent && completedFormData) {
                    setInitialMessage(messageContent);
                    setFormData(completedFormData);
                    setShowChat(true);
                  } else {
                    // Fallback: If stream data is corrupted, try to recover from database
                    // using the copyMessageId that was returned
                    console.warn('Incomplete stream data, attempting recovery...');
                    if (parsed.copyMessageId) {
                      try {
                        const recoveryHeaders: HeadersInit = { 'Content-Type': 'application/json' };
                        if (token) recoveryHeaders['Authorization'] = `Bearer ${token}`;
                        
                        const recoveryResponse = await fetch(`/api/copy-messages/${parsed.copyMessageId}`, {
                          headers: recoveryHeaders,
                          credentials: 'include',
                        });
                        
                        if (recoveryResponse.ok) {
                          const recoveredData = await recoveryResponse.json();
                          setInitialMessage(recoveredData.messageContent || messageContent || '');
                          setFormData(recoveredData.metadata?.formData || completedFormData || values);
                          setShowChat(true);
                        }
                      } catch (recoveryError) {
                        console.error('Recovery failed:', recoveryError);
                        // Last resort: use what we have
                        setInitialMessage(messageContent || '');
                        setFormData(completedFormData || values);
                        setShowChat(true);
                      }
                    } else {
                      // No copyMessageId, use form values as fallback
                      setInitialMessage(messageContent || '');
                      setFormData(completedFormData || values);
                      setShowChat(true);
                    }
                  }
                  
                  // Invalidate caches
                  queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
                  queryClient.invalidateQueries({ queryKey: ['/api/activities'] });
                  queryClient.invalidateQueries({ queryKey: ['/api/leaderboard'] });
                  queryClient.invalidateQueries({ queryKey: ['/api/global-stats'] });
                } else if (parsed.type === 'error') {
                  throw new Error(parsed.message);
                }
              } catch (e) {
                if (e instanceof Error && !e.message.includes('JSON')) {
                  throw e;
                }
              }
            }
          }
        }
        
        if (!hasCompleted) {
          // Stream ended without completion - attempt to recover by checking for recent copy
          console.warn('Stream ended without completion, attempting database recovery...');
          try {
            const recoveryHeaders: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) recoveryHeaders['Authorization'] = `Bearer ${token}`;
            
            // Fetch recent copy messages for this user/station/sponsor
            const recentCopyResponse = await fetch(
              `/api/stations/${stationId}/copy-messages/recent?sponsorName=${encodeURIComponent(values.sponsorName)}`,
              {
                headers: recoveryHeaders,
                credentials: 'include',
              }
            );
            
            if (recentCopyResponse.ok) {
              const recentCopy = await recentCopyResponse.json();
              if (recentCopy && recentCopy.messageContent) {
                // Found a recent copy - use it
                console.log('Successfully recovered copy from database');
                setInitialMessage(recentCopy.messageContent);
                setFormData(recentCopy.metadata?.formData || values);
                setShowChat(true);
                
                // Invalidate caches
                queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
                queryClient.invalidateQueries({ queryKey: ['/api/activities'] });
                queryClient.invalidateQueries({ queryKey: ['/api/leaderboard'] });
                queryClient.invalidateQueries({ queryKey: ['/api/global-stats'] });
                
                // Successfully recovered, break out
                setIsGenerating(false);
                return;
              }
            }
          } catch (recoveryError) {
            console.error('Database recovery failed:', recoveryError);
          }
          
          throw new Error('Stream ended without completion');
        }
        
        // Success! Break out of retry loop
        setIsGenerating(false);
        return;
        
      } catch (error: any) {
        lastError = error;
        const duration = Date.now() - startTime;
        
        // Check if this was a station-switch abort - don't retry in this case
        if (requestStationIdRef.current !== requestStationId) {
          console.log('Station changed - cancelling copy generation retry');
          setIsGenerating(false);
          return;
        }
        
        // Log timeout errors to API Status page
        if (error.name === 'AbortError' || error.message.includes('timeout')) {
          try {
            const logHeaders: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) logHeaders['Authorization'] = `Bearer ${token}`;
            
            await fetch('/api/log-client-error', {
              method: 'POST',
              headers: logHeaders,
              credentials: 'include',
              body: JSON.stringify({
                endpoint: `/api/stations/${stationId}/copy-engine/generate-initial`,
                errorMessage: `Request timeout after ${Math.round(duration / 1000)}s (attempt ${attempt}/${maxRetries})`,
                statusCode: 408, // 408 Request Timeout - proper HTTP code for timeout
                duration,
                apiType: 'internal',
                metadata: {
                  attemptNumber: attempt,
                  maxRetries,
                  sponsorName: values.sponsorName,
                  copyType: values.copyType,
                  isTimeout: true
                }
              }),
            });
          } catch (logError) {
            console.error('Failed to log timeout error:', logError);
          }
          
          // Continue to next retry attempt if not at max
          if (attempt < maxRetries) {
            continue;
          }
        }
        
        // For non-timeout errors or last attempt, show error and stop
        if (attempt === maxRetries) {
          toast({
            title: "Error",
            description: error.message || "Failed to generate copy after multiple attempts",
            variant: "destructive",
          });
          setProgressPercent(0);
          setProgressStage("");
          setIsGenerating(false);
          throw error;
        }
      }
    }
    
    // If we've exhausted all retries, show error
    if (lastError) {
      toast({
        title: "Error",
        description: "Failed to generate copy after multiple attempts. Please try again.",
        variant: "destructive",
      });
      setProgressPercent(0);
      setProgressStage("");
    }
    setIsGenerating(false);
  };

  const onSubmit = (values: CopyEngineFormValues) => {
    setIsFormMinimized(true);
    generateWithProgress(values);
  };


  if (stationLoading) {
    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto">
          <Skeleton className="h-10 w-1/3 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (stationError || !station) {
    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {stationError?.message || "Failed to load station"}
            </AlertDescription>
          </Alert>
        </div>
      </AppLayout>
    );
  }

  // Show form if not in chat mode
  if (!showChat) {
    return (
      <AppLayout>
        <StationToolLayout station={station}>
          <div className="space-y-6">
            <Card className={isGenerating ? "transition-all duration-300" : ""}>
              <CardHeader 
                className={isGenerating ? "cursor-pointer" : ""}
                onClick={() => isGenerating && setIsFormMinimized(!isFormMinimized)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Copy Engine
                    </CardTitle>
                    {isFormMinimized ? (
                      <CardDescription className="mt-3">
                        {form.getValues('sponsorName') || 'Sponsor'} • Click to edit
                      </CardDescription>
                    ) : (
                      <CardDescription className="mt-2">
                        {station.copyGeneratorDisclaimer || "Generate FCC-compliant sponsorship messages for on-air use"}
                      </CardDescription>
                    )}
                  </div>
                  {isGenerating && (
                    <Button variant="ghost" size="sm">
                      {isFormMinimized ? 
                        <ChevronDown className="h-4 w-4" /> : 
                        <ChevronUp className="h-4 w-4" />
                      }
                    </Button>
                  )}
                </div>
              </CardHeader>
              {!isFormMinimized && (<CardContent className="pt-2">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    {/* Sponsor Name */}
                    <FormField
                      control={form.control}
                      name="sponsorName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sponsor Name *</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter sponsor name" 
                              {...field} 
                              data-testid="input-sponsor-name"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Copy Type Selection */}
                    <FormField
                      control={form.control}
                      name="copyType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Copy Type *</FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="flex flex-col space-y-2"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="broadcast" id="broadcast" />
                                <Label htmlFor="broadcast" className="font-normal">
                                  Draft Copy for Broadcast
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="event" id="event" />
                                <Label htmlFor="event" className="font-normal">
                                  Draft Copy for an Event
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Conditional Fields for Broadcast */}
                    {copyType === "broadcast" && (
                      <>
                        <FormField
                          control={form.control}
                          name="sponsorWebsite"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Sponsor Website</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="example.com or https://example.com" 
                                  {...field} 
                                  data-testid="input-sponsor-website"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="previousCopy"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Previously Used Copy (if Applicable)</FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="Paste any previous sponsorship messages here" 
                                  rows={4}
                                  {...field} 
                                  data-testid="textarea-previous-copy"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="moreInfoLink"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>More Info Link (Link to Be Used in Copy)</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="example.com/vanity-URL" 
                                  {...field} 
                                  data-testid="input-more-info-link"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    )}

                    {/* Conditional Fields for Event */}
                    {copyType === "event" && (
                      <>
                        <FormField
                          control={form.control}
                          name="eventName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Event Name</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="Enter event name" 
                                  {...field} 
                                  data-testid="input-event-name"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="eventWebsite"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Event Website</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="example.com/event or https://example.com/event" 
                                  {...field} 
                                  data-testid="input-event-website"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="eventDatesAndTimes"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Event Dates and Times</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="e.g., March 15-17, 2025, 10am-6pm daily" 
                                  {...field} 
                                  data-testid="input-event-dates"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="eventLocation"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Event Location <span className="text-destructive">*</span></FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="e.g., Denver Convention Center" 
                                  {...field} 
                                  data-testid="input-event-location"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="ticketsLink"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>More Info / Tickets Link (Link to be used in copy) <span className="text-destructive">*</span></FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="example.com/tickets" 
                                  {...field} 
                                  data-testid="input-tickets-link"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    )}

                    {/* Additional Details - Common field */}
                    <FormField
                      control={form.control}
                      name="additionalDetails"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Additional Details to Include</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Include any key talking points, context, guidance or required language" 
                              rows={4}
                              {...field} 
                              data-testid="textarea-additional-details"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      disabled={isGenerating}
                      className="w-full"
                      data-testid="button-generate-copy"
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generating Copy...
                        </>
                      ) : (
                        "Generate Copy"
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>)}
            </Card>

            {/* Progress Interstitial */}
            {isGenerating && (
              <Card className="shadow-xl border-2 border-blue-400">
                <CardContent className="py-12">
                  <div className="flex flex-col items-center justify-center space-y-6">
                    {/* Animated GIF */}
                    <div className="relative">
                      <img 
                        src={thinkingAnimation} 
                        alt="Processing" 
                        className="w-[150px] h-[150px] object-contain"
                      />
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full max-w-2xl px-8">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>{progressStage}</span>
                        <span>{progressPercent}%</span>
                      </div>
                      <Progress value={progressPercent} className="w-full h-3" />
                      
                      {/* Current URL being processed */}
                      {currentUrl && (
                        <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                          <Link2 className="w-4 h-4" />
                          <span className="truncate">Processing: {currentUrl}</span>
                        </div>
                      )}
                    </div>

                    {/* Progress Stage Icons */}
                    <div className="relative flex justify-between w-full max-w-2xl px-8">
                      {[
                        { label: 'Initialize', icon: Search, percent: 10 },
                        { label: 'Validate URLs', icon: Link2, percent: 20 },
                        { label: 'Scrape Sites', icon: Globe, percent: 60 },
                        { label: 'Extract Context', icon: FileCode, percent: 80 },
                        { label: 'Generate Draft', icon: CheckCircle2, percent: 100 }
                      ].map((stage, index) => (
                        <div key={index} className="flex flex-col items-center">
                          <div className={`
                            w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300
                            ${progressPercent >= stage.percent
                              ? 'bg-blue-500 border-blue-500 text-white' 
                              : 'bg-gray-100 border-gray-300 text-gray-400'
                            }
                          `}>
                            <stage.icon className="w-5 h-5" />
                          </div>
                          <span className={`text-xs mt-2 transition-colors duration-300 ${
                            progressPercent >= stage.percent ? 'text-blue-600 font-semibold' : 'text-gray-500'
                          }`}>
                            {stage.label}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Progress Details */}
                    {progressDetails.length > 0 && (
                      <div className="w-full max-w-2xl px-8">
                        <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto">
                          <div className="space-y-1">
                            {progressDetails.map((detail, index) => (
                              <div key={index} className="text-xs text-gray-600 flex items-start gap-2">
                                <CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                <span>{detail}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </StationToolLayout>
      </AppLayout>
    );
  }

  // Show chat interface with the initial message from admin config + user input
  // CopyEngineChat component handles all hooks consistently
  // Guard against undefined formData
  if (!formData) {
    return (
      <AppLayout>
        <StationToolLayout station={station}>
          <div className="max-w-7xl mx-auto">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Invalid chat state. Please start a new copy request.
              </AlertDescription>
            </Alert>
          </div>
        </StationToolLayout>
      </AppLayout>
    );
  }
  
  return (
    <AppLayout>
      <StationToolLayout station={station}>
        <div className="h-[calc(100vh-12rem)]">
          <CopyEngineChat
            station={station}
            initialMessage={initialMessage}
            formData={formData}
            onNewCopyRequest={() => {
              setShowChat(false);
              setInitialMessage("");
              setFormData(undefined);
              setIsFormMinimized(false); // Expand the form when starting new request
              form.reset();
            }}
          />
        </div>
      </StationToolLayout>
    </AppLayout>
  );
}